/**
 * SOVRANOVA — Feedback (in-app, worldwide, voice-or-text)
 *
 * Captured at the moment of friction rather than recalled days later, and usable
 * from any timezone without email. Speaking is offered first because it costs
 * ~10 seconds instead of minutes of typing, carries tone, and is the accessible
 * route for people who won't or can't type much. Typing is equal weight.
 *
 * PRIVACY: we send only what the person deliberately wrote or spoke, plus
 * technical context to reproduce a problem (version, device, screen, region).
 * NEVER session, journal or check-in content. Audio is transcribed on-device and
 * discarded — only text is sent. The UI says so; the backend keeps that promise.
 *
 * No mandatory fields beyond a non-empty message: every extra required box costs
 * completion, and completion is the whole point.
 */
import { useState, useRef } from 'react'
import { api } from '../../services/api'
import { resolveRegion } from '../../services/crisisResources'

const CATEGORIES = [
  { id: 'broke',     emoji: '🔧', label: 'Something broke' },
  { id: 'confusing', emoji: '🤔', label: 'Something confusing' },
  { id: 'idea',      emoji: '💡', label: 'An idea' },
  { id: 'disliked',  emoji: '👎', label: "I didn't like something" },
  { id: 'loved',     emoji: '💛', label: 'Something I loved' },
]

export default function FeedbackView({ onHome, screen }) {
  const [body, setBody] = useState('')
  const [category, setCategory] = useState(null)
  const [source, setSource] = useState('text')
  const [recording, setRecording] = useState(false)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState(null)
  const recRef = useRef(null)

  // Browser speech-to-text. Transcript only — no audio leaves the device.
  const canSpeak = typeof window !== 'undefined' &&
    !!(window.SpeechRecognition || window.webkitSpeechRecognition)

  function toggleRecording() {
    if (recording) { try { recRef.current?.stop() } catch (_) {} ; setRecording(false); return }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR) return
    const rec = new SR()
    rec.continuous = true
    rec.interimResults = false
    rec.lang = navigator.language || 'en-AU'
    rec.onresult = (e) => {
      let chunk = ''
      for (let i = e.resultIndex; i < e.results.length; i++) chunk += e.results[i][0].transcript
      if (chunk.trim()) {
        setBody(prev => (prev ? (prev + ' ' + chunk).trim() : chunk.trim()))
        setSource('voice')
      }
    }
    rec.onerror = () => { setRecording(false); setError('Could not hear that — you can type it instead.') }
    rec.onend = () => setRecording(false)
    recRef.current = rec
    setError(null)
    try { rec.start(); setRecording(true) } catch (_) { setRecording(false) }
  }

  async function send() {
    const text = body.trim()
    if (!text || busy) return
    setBusy(true); setError(null)
    let timezone = null
    try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone } catch (_) {}
    try {
      await api.post('/feedback', {
        body: text,
        category,
        source,
        screen: screen || null,
        region: resolveRegion(),
        timezone,
        device: (navigator.userAgent || '').slice(0, 200),
      })
      setSent(true)
    } catch (_) {
      setError("That didn't send. Please try again, or message David directly.")
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <div style={s.wrap}>
        <div style={s.card}>
          <p style={s.thanksIcon}>💛</p>
          <h2 style={s.title}>Got it. Thank you.</h2>
          <p style={s.lead}>David reads every one of these. If it changes something, you'll hear about it.</p>
          <button style={s.primary} onClick={() => { setSent(false); setBody(''); setCategory(null); setSource('text') }}>
            Send another
          </button>
          <button style={s.ghost} onClick={onHome}>Back</button>
        </div>
      </div>
    )
  }

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <h2 style={s.title}>What's on your mind about SOVRANOVA?</h2>
        <p style={s.lead}>
          Anything at all — what broke, what confused you, what you'd change, what you didn't
          like, what you'd miss if it vanished. <strong>Blunt is more useful than kind.</strong>
        </p>

        {canSpeak && (
          <button style={{ ...s.mic, ...(recording ? s.micOn : {}) }}
            onClick={toggleRecording} aria-pressed={recording}>
            {recording ? '● Listening — tap to stop' : '🎤 Speak it'}
          </button>
        )}

        <textarea style={s.textarea} value={body} rows={5}
          onChange={(e) => setBody(e.target.value)}
          placeholder={canSpeak ? 'Or type it here…' : 'Type it here…'} />

        <p style={s.optional}>Optional — what kind of thing is it?</p>
        <div style={s.chips}>
          {CATEGORIES.map(c => (
            <button key={c.id}
              style={{ ...s.chip, ...(category === c.id ? s.chipOn : {}) }}
              onClick={() => setCategory(category === c.id ? null : c.id)}>
              <span aria-hidden="true">{c.emoji}</span> {c.label}
            </button>
          ))}
        </div>

        {error && <p style={s.error}>{error}</p>}

        <button style={{ ...s.primary, opacity: body.trim() && !busy ? 1 : 0.5 }}
          onClick={send} disabled={!body.trim() || busy}>
          {busy ? 'Sending…' : 'Send'}
        </button>
        <button style={s.ghost} onClick={onHome}>Cancel</button>

        <p style={s.privacy}>
          We attach your app version and device type so David can reproduce problems.
          <strong> Nothing from your sessions, journal or check-ins is included.</strong>
          {/* Was "the audio never leaves your phone" — not true in Chrome, where
              the Web Speech API sends the audio to the browser's own speech
              service to transcribe it. Only the text reaches SOVRANOVA, which is
              the part we can actually promise. */}
          {canSpeak ? ' If you speak it, your device transcribes it and only the text reaches us — we never receive the recording.' : ''}
        </p>
      </div>
    </div>
  )
}

const s = {
  wrap:       { minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '1.25rem 1rem 3rem' },
  card:       { width: '100%', maxWidth: 620, display: 'flex', flexDirection: 'column', gap: '0.7rem' },
  title:      { margin: '0.5rem 0 0', fontSize: '1.35rem', lineHeight: 1.25 },
  lead:       { margin: 0, opacity: 0.85, fontSize: '0.95rem', lineHeight: 1.5 },
  mic:        { padding: '0.95rem 1rem', borderRadius: 999, border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.06)', color: 'inherit', fontSize: '1rem', fontWeight: 600, cursor: 'pointer' },
  micOn:      { background: 'rgba(220,90,90,0.22)', borderColor: 'rgba(240,120,120,0.6)' },
  textarea:   { width: '100%', boxSizing: 'border-box', padding: '0.85rem', borderRadius: 14, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'inherit', fontSize: '1rem', lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit' },
  optional:   { margin: '0.3rem 0 0', fontSize: '0.85rem', opacity: 0.7 },
  chips:      { display: 'flex', flexWrap: 'wrap', gap: '0.45rem' },
  chip:       { padding: '0.5rem 0.75rem', borderRadius: 999, border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: 'inherit', fontSize: '0.85rem', cursor: 'pointer' },
  chipOn:     { background: 'rgba(255,255,255,0.16)', borderColor: 'rgba(255,255,255,0.45)' },
  primary:    { marginTop: '0.5rem', padding: '0.95rem 1rem', borderRadius: 999, border: 'none', background: 'rgba(200,120,90,0.9)', color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: 'pointer' },
  ghost:      { padding: '0.7rem 1rem', borderRadius: 999, border: 'none', background: 'transparent', color: 'inherit', opacity: 0.7, fontSize: '0.9rem', cursor: 'pointer' },
  privacy:    { margin: '0.6rem 0 0', fontSize: '0.78rem', opacity: 0.65, lineHeight: 1.5 },
  error:      { margin: 0, fontSize: '0.85rem', color: '#ffb4b4' },
  thanksIcon: { fontSize: '2rem', margin: '1rem 0 0' },
}
